import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import { z } from "zod";

export type MessageFileInfoJSON = z.infer<typeof MessageFileInfoSchema.MessageFileInfoJSONZ>;
export type MessageFileInfoParams = z.infer<typeof MessageFileInfoSchema.MessageFileInfoZ>;

export default class MessageFileInfoSchema {
  public static readonly MessageFileInfoJSONZ = z.object({
    id: z.uuid(),
    name: z.string(),
    fileSize: z.number(),
    mimeType: z.string().nullish(),
    createdDate: z.iso.datetime({ offset: true }).nullish(),
    rawData: z.instanceof(Blob).nullish(),
    dataUri: z.string().nullish(),
  });

  public static readonly MessageFileInfoZ = z.object({
    id: z.instanceof(Guid),
    name: z.string(),
    fileSize: z.number(),
    mimeType: z.string(),
    createdDate: DateSchema.type,
    rawData: z.instanceof(Blob).optional(),
    dataUri: z.string().optional(),
  });

  public static validate(values: unknown): MessageFileInfoParams {
    return parseOrThrow(MessageFileInfoSchema.MessageFileInfoZ, values, {
      logError: true,
      schemaName: "MessageFileInfoSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): MessageFileInfoJSON {
    return parseOrThrow(MessageFileInfoSchema.MessageFileInfoJSONZ, json, {
      logError: true,
      schemaName: "MessageFileInfoSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is MessageFileInfoParams {
    try {
      MessageFileInfoSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is MessageFileInfoJSON {
    try {
      MessageFileInfoSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: MessageFileInfoJSON): MessageFileInfoParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(MessageFileInfoSchema.MessageFileInfoJSONZ, json, {
      logError: true,
      schemaName: "MessageFileInfoSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return MessageFileInfoSchema.codec.decode(parsed);
  }

  public static serialize(params: MessageFileInfoParams): MessageFileInfoJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = MessageFileInfoSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return MessageFileInfoSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    MessageFileInfoSchema.MessageFileInfoJSONZ,
    MessageFileInfoSchema.MessageFileInfoZ,
    {
      decode: (data) => ({
        id: new Guid(GuidSchema.deserialize(data.id)),
        name: data.name,
        fileSize: data.fileSize ?? 0,
        mimeType: data.mimeType ?? "application/octet-stream",
        createdDate: new AHDate(DateSchema.deserialize(data.createdDate)),
        dataUri: data.dataUri ?? undefined,
      }),
      encode: (fileInfo) => ({
        id: fileInfo.id.value,
        name: fileInfo.name,
        fileSize: fileInfo.fileSize,
        mimeType: fileInfo.mimeType,
        createdDate: fileInfo.createdDate.toJSON(),
        dataUri: fileInfo.dataUri,
      }),
    }
  );
}
